import { SidebarMenu } from "components";
import { useRef, useState, useEffect } from "react";
import SellersOrders from "../components/SellersOrders";
import CustomersVisitControl from "../components/CustomersVisitControl";
import { formatDateToLocaleDateString, formatDateToISODateString, formatISODateStringToDate, exportToExcel } from "utils";
import axios from "axios";
import { useAppSelector } from "hooks";
import { CircularProgress, Box, Tabs, Tab, Button, Popover, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Checkbox, Divider, Typography } from "@mui/material";

function DateRangeControl({
	systemMin,
	today,
	uiStart,
	setUiStart,
	uiEnd,
	setUiEnd,
	onApply,
}: {
	systemMin: Date;
	today: Date;
	uiStart: Date;
	setUiStart: (d: Date) => void;
	uiEnd: Date;
	setUiEnd: (d: Date) => void;
	onApply: () => void;
}) {
	const MAX_RANGE_DAYS = 44
	const ONE_DAY = 24 * 60 * 60 * 1000;

	const toMidnight = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);

	function clampToSystem(date: Date) {
		let x = toMidnight(date);
		const min = toMidnight(systemMin);
		const max = toMidnight(today);
		if (x < min) x = min;
		if (x > max) x = max;
		return x;
	}

	function normalizeWithAnchor(start: Date, end: Date, anchor: "start" | "end") {
		let s = clampToSystem(start);
		let e = clampToSystem(end);

		if (anchor === "start") {
			const maxEnd = new Date(s.getTime() + MAX_RANGE_DAYS * ONE_DAY);
			const cappedMaxEnd = maxEnd > today ? today : maxEnd;
			if (e < s) e = cappedMaxEnd;
			if (e > cappedMaxEnd) e = cappedMaxEnd;
		} else {
			const minStart = new Date(e.getTime() - MAX_RANGE_DAYS * ONE_DAY);
			const cappedMinStart = minStart < systemMin ? systemMin : minStart;
			if (s > e) s = cappedMinStart;
			if (s < cappedMinStart) s = cappedMinStart;
		}

		return [s, e] as const;
	}

	const handleStartInput = (ev: React.ChangeEvent<HTMLInputElement>) => {
		const raw = ev.target.value;
		const picked = formatISODateStringToDate(raw, false);
		if (!picked) return;
		const [s, e] = normalizeWithAnchor(picked, uiEnd, "start");
		setUiStart(s);
		setUiEnd(e);
	};

	const handleEndInput = (ev: React.ChangeEvent<HTMLInputElement>) => {
		const raw = ev.target.value;
		const picked = formatISODateStringToDate(raw, false);
		if (!picked) return;
		const [s, e] = normalizeWithAnchor(uiStart, picked, "end");
		setUiStart(s);
		setUiEnd(e);
	};

	return (
		<div className="flex items-end gap-4">
			<div className="flex flex-col">
				<label className="text-sm font-semibold mb-1">Fecha de inicio</label>
				<input
					type="date"
					className="border rounded-md px-3 py-2"
					min={formatDateToISODateString(systemMin)}
					max={formatDateToISODateString(today)}
					value={formatDateToISODateString(uiStart)}
					onChange={handleStartInput}
				/>
			</div>

			<div className="flex flex-col">
				<label className="text-sm font-semibold mb-1">Fecha de fin</label>
				<input
					type="date"
					className="border rounded-md px-3 py-2"
					min={formatDateToISODateString(systemMin)}
					max={formatDateToISODateString(today)}
					value={formatDateToISODateString(uiEnd)}
					onChange={handleEndInput}
				/>
			</div>

			<button
				className="bg-primary text-white px-4 py-2 rounded-md font-semibold"
				onClick={onApply}
			>
				Aplicar
			</button>
		</div>
	);
}

export const SellersControlPage = () => {
	const { token } = useAppSelector((state) => state.user);
	const today = new Date(new Date().getFullYear(), new Date().getMonth(), 1, 0, 0, 0, 0);
	const systemMin = new Date(2024, 0, 1);

	const [startDateSearchFilter, setStartDateSearchFilter] = useState<Date>(today);
	const [finishDateSearchFilter, setFinishDateSearchFilter] = useState<Date>(new Date());

	const contentRef = useRef<HTMLDivElement | null>(null);
	const [selectedVendors, setSelectedVendors] = useState<string[] | null>(null);
	const [vendorList, setVendorList] = useState<Array<{ code: string; name: string }>>([]);
	const [loader, setLoader] = useState<boolean>(true);
	const [dataReport, setDataReport] = useState<any>({});
	const [backupDataReport, setBackupDataReport] = useState<any>({});
	const [activeReportTab, setActiveReportTab] = useState<"visits" | "orders">("visits");
	const [vendorAnchorEl, setVendorAnchorEl] = useState<HTMLElement | null>(null);

	useEffect(() => {
		if (token) {
			const controller = new AbortController();
			getDataInitial(controller.signal);
			return () => controller.abort();
		}
	}, []); // eslint-disable-line

	const getDataInitial = async (signal: AbortSignal) => {
		setLoader(true);
		const response = await axios.get(`${process.env.REACT_APP_SERVICE_URL}/documents/get/v2/compliance/range`, {
			params: {
				dayInit: formatDateToISODateString(startDateSearchFilter),
				dayEnd: formatDateToISODateString(finishDateSearchFilter),
			},
			headers: {
				Authorization: `UserToken ${token || ""}`,
			},
			signal,
		});

		const listUser = (response.data?.data?.ordersResume || []).map((item: any) => ({
			code: item.userId,
			name: item.vendorName
		}));

		const withAll = [{ code: "all", name: "Todos" }, ...listUser];

		setVendorList(withAll);
		setDataReport(response.data.data || {})
		setBackupDataReport(response.data.data || {})
		setLoader(false);
	}

	const openVendorPopover = (ev: React.MouseEvent<HTMLElement>) => setVendorAnchorEl(ev.currentTarget);
	const closeVendorPopover = () => setVendorAnchorEl(null);
	const vendorPopoverOpen = Boolean(vendorAnchorEl);

	const toggleVendor = (code: string) => {
		if (code === "all") {
			setSelectedVendors(null);
			return;
		}

		if (selectedVendors === null) {
			setSelectedVendors([code]);
			return;
		}

		if (selectedVendors.includes(code)) {
			const next = selectedVendors.filter((c) => c !== code);
			setSelectedVendors(next.length === 0 ? null : next);
		} else setSelectedVendors([...selectedVendors, code]);
	};

	const isVendorChecked = (code: string) => {
		if (code === "all") return selectedVendors === null;
		if (selectedVendors === null) return true;
		return selectedVendors.includes(code);
	};

	const includeByVendor = (item: any) => {
		if (selectedVendors === null) return true;
		const codeCandidates = [item.userId, item.salesPersonCode, item.vendorCode, item.vendorId].filter(Boolean);
		return codeCandidates.some((c) => selectedVendors.includes(String(c)));
	};

	const handleExportExcel = () => {
		let payload: any[] = [];
		if (activeReportTab === "visits") {
			const raw = Array.isArray(backupDataReport?.ordersResume) ? backupDataReport.ordersResume : [];
			payload = raw.filter(includeByVendor);
		} else {
			const raw = Array.isArray(dataReport?.details) ? dataReport.details : [];
			payload = raw.filter(includeByVendor);
		}
		exportToExcel(payload, activeReportTab === "visits" ? "efectividad_visitas" : "ordenes_vendedores", activeReportTab === "visits" ? "Visitas" : "Órdenes");
	};

	const filteredVisits = (Array.isArray(backupDataReport?.ordersResume) ? backupDataReport.ordersResume : []).filter(includeByVendor);
	const filteredDetails = (Array.isArray(dataReport?.details) ? dataReport.details : []).filter(includeByVendor);

	return (
		<div ref={contentRef}>
			<div className="w-full h-screen grid grid-cols-12">
				<div className="col-span-2 border-r-2 border-light">
					<SidebarMenu pageName="sellers_control" />
				</div>

				<div className="w-full h-screen flex flex-col flex-grow col-span-10">
					<div className="w-full flex justify-between items-center border-b-2 border-tertiary px-2 py-3">
						<div className="flex flex-col items-start gap-2">
							<p className="font-semibold text-lg">
								Del {formatDateToLocaleDateString(startDateSearchFilter)} al {formatDateToLocaleDateString(finishDateSearchFilter)}
							</p>
						</div>

						<div className="flex items-center gap-6">
							<DateRangeControl
								systemMin={systemMin}
								today={today}
								uiStart={startDateSearchFilter}
								setUiStart={setStartDateSearchFilter}
								uiEnd={finishDateSearchFilter}
								setUiEnd={setFinishDateSearchFilter}
								onApply={() => {
									const controller = new AbortController();
									getDataInitial(controller.signal);
								}}
							/>

							<div>
								<Button variant="outlined" onClick={openVendorPopover}>
									Vendedores {selectedVendors === null ? "(Todos)" : `(${selectedVendors.length})`}
								</Button>
								<Popover
									open={vendorPopoverOpen}
									anchorEl={vendorAnchorEl}
									onClose={closeVendorPopover}
									anchorOrigin={{
										vertical: 'bottom',
										horizontal: 'left',
									}}
								>
									<div style={{ width: 300, maxHeight: 360, overflow: 'auto' }}>
										<List dense>
											<ListItem key="label">
												<ListItemText>
													<Typography variant="subtitle1">Filtrar por vendedor</Typography>
												</ListItemText>
											</ListItem>
											<Divider />
											{vendorList.map((v) => (
												<ListItemButton key={v.code} onClick={() => toggleVendor(v.code)}>
													<ListItemIcon>
														<Checkbox
															edge="start"
															checked={isVendorChecked(v.code)}
															tabIndex={-1}
															disableRipple
															inputProps={{ 'aria-labelledby': `checkbox-list-label-${v.code}` }}
														/>
													</ListItemIcon>
													<ListItemText id={`checkbox-list-label-${v.code}`} primary={v.name} secondary={v.code !== "all" ? v.code : undefined} />
												</ListItemButton>
											))}
											<Divider />
											<ListItem>
												<Button
													fullWidth
													onClick={() => setSelectedVendors(null)}>
													Mostrar todos
												</Button>
											</ListItem>
										</List>
									</div>
								</Popover>
							</div>

							<button onClick={handleExportExcel} className="bg-primary text-white px-4 py-2 mt-6 rounded">
								Descargar Excel
							</button>
						</div>
					</div>

					<Box sx={{ borderBottom: 1, borderColor: "divider", bgcolor: "white" }}>
						<Tabs
							value={activeReportTab}
							onChange={(_, v) => setActiveReportTab(v)}
							indicatorColor="primary"
							textColor="primary"
						>
							<Tab value="visits" label="Efectividad de visitas" />
							<Tab value="orders" label="Órdenes de vendedores" />
						</Tabs>
					</Box>

					<div style={{ flex: 1, overflow: "auto" }}>
						{loader ? (
							<div className="p-6 flex justify-center">
								<CircularProgress />
							</div>
						) : activeReportTab === "visits" ? (
							<CustomersVisitControl dataReport={filteredVisits || []} />
						) : (
							<SellersOrders dataReport={filteredDetails || []} />
						)}
					</div>
				</div>
			</div>
		</div>
	);
};
